import React from 'react';
import { Link } from 'react-router-dom';
import {
  PageHead, Section, Eyebrow, PageTitle, Lede, SectionHead, Button, Card, CardTitle, CardBody,
  Grid, Saying, CONTACT_MAILTO,
} from '../components/ui';

/**
 * What we do. Three services, the free instrument, and the frameworks in
 * one line each. The CoThink Discipline is the spine: Diagnose, Drill, Operate.
 */

const SPINE = [
  { step: 'Diagnose', body: 'See where you stand and where the work stands. The Compass places a person on the AI Fluency Ladder; the Rumsfeld Radar maps what the organization knows and does not.' },
  { step: 'Drill', body: 'Build the capabilities under pressure, on real work, with a coach who can see what you cannot see about your own swing.' },
  { step: 'Operate', body: 'Run the work and govern it. The frameworks your own people keep using after we leave.' },
];

const Service = ({ id, eyebrow, head, line, body, points, cta, ctaHref, ctaTo, secondary }) => (
  <article id={id} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 border-t border-navy-200 dark:border-navy-700 scroll-mt-24">
    <div className="md:col-span-4">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h3 className="font-display font-bold text-subhead text-navy-800 dark:text-white">{head}</h3>
      <p className="font-display text-[19px] mt-3 text-emerald-600 dark:text-emerald-300">{line}</p>
    </div>
    <div className="md:col-span-8">
      <p className="prose-rbl">{body}</p>
      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {points.map((p) => (
          <li key={p} className="font-sans text-interface text-navy-600 dark:text-[#C9D2E0] pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[9px] before:h-2 before:w-2 before:rounded-full before:bg-emerald-500">{p}</li>
        ))}
      </ul>
      <div className="mt-6 flex flex-wrap gap-3">
        {ctaTo ? <Button to={ctaTo}>{cta}</Button> : <Button href={ctaHref}>{cta}</Button>}
        {secondary}
      </div>
    </div>
  </article>
);

const HowWeHelpPage = () => (
  <>
    <PageHead
      title="What we do"
      description="AI Transformation Advisory, Executive Education at OSU Fisher, and the Apprenticeship. How Right Brain Labs works: Diagnose, Drill, Operate."
      path="/what-we-do"
    />

    <Section tone="page" reveal={false}>
      <Eyebrow>What we do</Eyebrow>
      <PageTitle>AI Fluency is a practice. We help you build it and keep it.</PageTitle>
      <Lede>We're an AI capability incubator. We provide the instrument to measure it, the frameworks to practice it against real work, and the services that build it inside your organization and leave it running after we leave.</Lede>
    </Section>

    <Section tone="card" id="how-we-work" className="scroll-mt-20">
      <Eyebrow>How we work</Eyebrow>
      <SectionHead>The CoThink Discipline: Diagnose, Drill, Operate.</SectionHead>
      <p className="prose-rbl mt-4">A practitioner diagnoses before they drill. They drill before they operate. They keep diagnosing and drilling while they operate, because the Mastery Loop never stops.</p>
      <Grid cols={3} className="mt-8">
        {SPINE.map((s, i) => (
          <Card key={s.step} accent>
            <div className="font-mono text-meta text-navy-500 dark:text-navy-400 mb-2">0{i + 1}</div>
            <CardTitle>{s.step}</CardTitle>
            <CardBody>{s.body}</CardBody>
          </Card>
        ))}
      </Grid>
    </Section>

    <Section tone="page" id="services" className="scroll-mt-20">
      <Eyebrow>Services</Eyebrow>
      <SectionHead>Three ways in. One outcome: a practice your people run without us.</SectionHead>
      <p className="prose-rbl mt-4">They build on each other. Advisory sets the baseline and the direction. Fisher shifts the frame for the executive team. The Apprenticeship builds the capability that stays.</p>

      <div className="mt-10">
        <Service
          id="advisory"
          eyebrow="For leaders, boards, and organizations"
          head="AI Transformation Advisory"
          line="Strategy, governance, and capability, built on 35 years of practice."
          body="Advisory for executives and boards facing the decisions AI forces: what it changes about the business, what to govern, and what capability to build first. From an individual baseline to an enterprise AI strategy, grounded in practitioner experience at IBM, Nationwide, and Magellan Health."
          points={[
            'Board-level AI Fluency: the questions to ask, the decisions to govern, and how to stay in the judgment seat',
            'AI strategy that compounds human capability instead of dependency',
            'The CoThink Compass as the baseline before any advisory work begins',
            'Keynotes grounded in the CoThink Discipline and the book',
          ]}
          cta="Start a conversation" ctaHref={CONTACT_MAILTO}
        />
        <Service
          id="osu-fisher"
          eyebrow="For executive teams"
          head="Executive Education at OSU Fisher"
          line="Where executives find out what AI Fluency asks of them."
          body="Delivered through the Fisher College of Business at The Ohio State University. One-day and multi-session intensives that shift the frame from tool adoption to capability development, with Ohio State credentialing. More than 100 executives have come through the AI Jumpstart for Executives in the first year. The Apprenticeship is the natural next step."
          points={[
            'Four domains: Collaborating, Leading, Operating, and Succeeding with AI',
            'Executive education pricing with Ohio State credentialing',
            'From individual practice to enterprise-wide strategy',
            'Built for organizations still working out what AI means for them',
          ]}
          cta="Ask about the next cohort" ctaHref={CONTACT_MAILTO}
          secondary={<Button href="https://fisher.osu.edu/executive-education" variant="secondary">OSU Fisher Executive Education</Button>}
        />
        <Service
          id="apprenticeship"
          eyebrow="For leadership teams, 10 to 15 participants"
          head="The Apprenticeship"
          line="The Apprenticeship ends. The capability stays."
          body="A finite, cohort-based engagement in which your practitioners take on a real organizational problem under practitioner coaching. Training tells you how to swing. The Apprenticeship is the batting cage where you swing, the field where you play, and the coach who tells you what you cannot see about your own swing. Capability transfers through demonstrated work, and the cohort leaves owning it."
          points={[
            'A cohort of 10 to 15 from the same organization, 30 to 60 days, immersive sessions and structured work between them',
            'The frameworks applied in sequence to a real problem: Rumsfeld Radar, AI Spectrum, Value Flow Framework, the Steering Cascade',
            'Selected practitioners earn the AI Artisan designation, recognized by peers and demonstrated through work',
            'A regimen your own people run afterward: game reps, a practitioner commons, and film study, in a 70-20-10 proportion',
          ]}
          cta="Talk to us about an Apprenticeship" ctaHref={CONTACT_MAILTO}
        />
      </div>
      <Saying tag="Cognitive Agility" className="mt-12">What you no longer practice, you no longer possess.</Saying>
    </Section>

    <Section tone="card" id="frameworks" className="scroll-mt-20">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <Eyebrow>Frameworks</Eyebrow>
          <SectionHead>The tools practitioners think with.</SectionHead>
        </div>
        <Button to="/frameworks" variant="ghost">All five frameworks</Button>
      </div>
      <p className="prose-rbl">Five frameworks on the Diagnose, Drill, Operate spine: the Rumsfeld Radar and the AI Spectrum to see where the work stands, the Value Flow Framework to remove waste before AI touches it, the Steering Cascade to govern, and the Practitioner Commons to keep the learning inside your organization. <Link to="/frameworks" className="text-emerald-600 dark:text-emerald-300 font-sans font-medium">Read them here.</Link></p>
    </Section>
  </>
);

export default HowWeHelpPage;
